import { useEffect } from 'react'
import { FaCircleCheck, FaTriangleExclamation, FaXmark } from 'react-icons/fa6'
import {
  adminNav,
} from '../features/admin/adminConfig'
import { AdminHero, AdminSidebar, AdminTopbar, navigateAdminSection } from '../features/admin/AdminLayout'
import { AdminOverview } from '../features/admin/AdminOverview'
import { AdminSection } from '../features/admin/AdminSection'
import { AdminDashboardSkeleton } from '../features/admin/AdminSkeletons'
import { useAdminDashboardApi } from '../features/admin/useAdminDashboardApi'
import { useAdminDashboardData } from '../features/admin/useAdminDashboardData'
import './Dashboard.css'

function AdminDashboard({ section = 'overview' }) {
  const activeSection = section
  const { adminData, apiStatus, apiToken, clearAdminStatus, downloadAdminCsv, isAdminLoading, refreshAdmin } = useAdminDashboardApi()
  const activeLabel = adminNav.find((item) => item.id === activeSection)?.label || 'Overview'
  const shouldShowAdminToast = Boolean(apiStatus) && !apiStatus.startsWith('Connecting')
  const adminStatusTone = /failed|could not|expired|required|error|blocked|invalid|denied/i.test(apiStatus) ? 'danger' : 'success'
  const navigateSection = (nextSection) => {
    navigateAdminSection(nextSection)
  }

  useEffect(() => {
    if (!shouldShowAdminToast) return undefined

    const timeout = window.setTimeout(() => {
      clearAdminStatus()
    }, adminStatusTone === 'danger' ? 6500 : 4200)

    return () => window.clearTimeout(timeout)
  }, [adminStatusTone, apiStatus, clearAdminStatus, shouldShowAdminToast])

  const {
    liveAuditRows,
    liveAuctionRows,
    liveBidRows,
    liveConsignmentRows,
    liveKpis,
    liveListingRows,
    liveOrderRows,
    liveUserRows,
    operationalReports,
  } = useAdminDashboardData(adminData)

  return (
    <section className="obc-dashboard obc-admin">
      <AdminSidebar activeSection={activeSection} onSectionChange={navigateSection} />

      <main className="obc-dashboard__main">
        <AdminTopbar activeLabel={activeLabel} onSectionChange={navigateSection} />

        <div className="obc-dashboard__canvas">
          {shouldShowAdminToast ? (
            <div className={`obc-admin__toast is-${adminStatusTone}`} role={adminStatusTone === 'danger' ? 'alert' : 'status'} aria-live={adminStatusTone === 'danger' ? 'assertive' : 'polite'}>
              <span className="obc-admin__toast-icon">
                {adminStatusTone === 'danger' ? <FaTriangleExclamation /> : <FaCircleCheck />}
              </span>
              <span className="obc-admin__toast-message">{apiStatus}</span>
              <button type="button" onClick={clearAdminStatus} aria-label="Dismiss notification">
                <FaXmark />
              </button>
            </div>
          ) : null}

          <AdminHero activeLabel={activeLabel} activeSection={activeSection} onSectionChange={navigateSection} />
          {isAdminLoading ? (
            <AdminDashboardSkeleton activeSection={activeSection} />
          ) : activeSection === 'overview' ? (
            <AdminOverview
              apiToken={apiToken}
              auditRows={liveAuditRows}
              auctionRows={liveAuctionRows}
              bidRows={liveBidRows}
              kpis={liveKpis}
              onRefresh={refreshAdmin}
              onSectionChange={navigateSection}
              orderRows={liveOrderRows}
              reports={operationalReports}
            />
          ) : (
            <AdminSection
              apiToken={apiToken}
              auctionRows={liveAuctionRows}
              bidRows={liveBidRows}
              consignmentRows={liveConsignmentRows}
              listingRows={liveListingRows}
              onExportCsv={downloadAdminCsv}
              onRefresh={refreshAdmin}
              onSectionChange={navigateSection}
              orderRows={liveOrderRows}
              section={activeSection}
              userRows={liveUserRows}
            />
          )}
        </div>
      </main>
    </section>
  )
}

export default AdminDashboard
